import React, { useState, useEffect } from 'react';
import io from 'socket.io-client';
import { useConfig } from '../context/ConfigContext';
import api from '../api/axios';

export default function Podium() {
  const { config } = useConfig();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  const FTP_BASE = 'https://ftp.pcnpublicidad.com/boehringer/concentrese';
  const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5070').replace(/\/api\/?$/, '');

  const fetchPodium = async () => {
    try {
      const res = await api.get('/points/podium');
      setLeaderboard(res.data);
    } catch (err) {
      console.error('Error al cargar el podio de ganadores:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPodium();
    const socket = io(API_BASE_URL);
    socket.on('podium_update', () => {
      fetchPodium();
    });
    return () => socket.disconnect();
  }, []);

  const toCapitalize = (str) => {
    if (!str || typeof str !== 'string') return '';
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word ? word.charAt(0).toUpperCase() + word.slice(1) : '')
      .join(' ');
  };

  const first = leaderboard[0];
  const second = leaderboard[1];
  const third = leaderboard[2];
  const rest = leaderboard.slice(3);

  return (
    <div
      className="min-h-screen bg-[#07241c] text-white flex flex-col items-center p-4 sm:p-6 space-y-6 bg-cover bg-center animate-fadeIn"
      style={{ backgroundImage: `url(${FTP_BASE}/fondo.png)` }}
    >
      {/* Podio Header */}
      <div className="w-full max-w-4xl bg-[#0b382c]/90 border-2 border-[#00e676]/50 rounded-3xl p-6 sm:p-8 shadow-2xl relative overflow-hidden backdrop-blur-md text-center">
        <span className="bg-[#00e676]/20 text-[#00e676] border border-[#00e676]/40 text-xs font-black px-4 py-1.5 rounded-full uppercase tracking-widest inline-block mb-2">
          Fórmula 1 — Tabla de Clasificación
        </span>
        <h1 className="text-2xl sm:text-4xl font-black text-white tracking-tight uppercase">
          Podio de Ganadores
        </h1>
        <p className="text-xs sm:text-sm text-slate-200 mt-2 font-medium max-w-xl mx-auto leading-relaxed">
          Clasificación en tiempo real de los pilotos con mayor puntaje acumulado en el circuito.
        </p>
        <div className="mt-3 inline-flex items-center gap-2 bg-[#07241c] border border-[#00e676]/40 px-3.5 py-1 rounded-full text-xs text-[#00e676] font-bold">
          <span className="w-2 h-2 rounded-full bg-[#00e676] animate-ping" />
          Actualización en Tiempo Real
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center text-slate-300 font-bold bg-[#0b382c]/80 rounded-3xl border border-[#00e676]/40 shadow-xl max-w-4xl w-full">
          Cargando tabla de posiones del circuito...
        </div>
      ) : leaderboard.length === 0 ? (
        <div className="w-full max-w-4xl bg-[#0b382c]/90 rounded-3xl border-4 border-[#00e676]/60 p-12 text-center shadow-2xl backdrop-blur-md">
          <div className="text-5xl mb-4">🏎️</div>
          <h3 className="text-xl font-black text-white uppercase tracking-wide">No Hay Usuarios en el Podio</h3>
          <p className="text-xs text-slate-300 mt-2 max-w-md mx-auto leading-relaxed">
            Estimados participantes, aún no hay registros de puntos. Visiten y escaneen los códigos QR de las estaciones para posicionarse en el podio.
          </p>
        </div>
      ) : (
        <div className="w-full max-w-4xl space-y-6">
          {/* Visual F1 Podium Structure (Top 3) */}
          <div className="bg-[#0b382c]/90 border-4 border-[#00e676]/60 rounded-3xl p-6 sm:p-8 shadow-2xl backdrop-blur-md">
            <div className="flex justify-center items-end gap-3 sm:gap-6 pt-4 pb-2">
              
              {/* 2° LUGAR (Plata - F1 Podium Left Step) */}
              {second ? (
                <div className="flex flex-col items-center w-28 sm:w-40">
                  <div className="text-center mb-3">
                    <span className="inline-block bg-slate-200 text-slate-900 text-[10px] sm:text-xs font-black px-2.5 py-0.5 rounded-full uppercase tracking-wider shadow-md mb-1">
                      2° LUGAR
                    </span>
                    <p className="font-bold text-white text-xs sm:text-sm truncate max-w-[120px] sm:max-w-[150px]" title={second.full_name}>
                      {toCapitalize(second.full_name)}
                    </p>
                    <p className="text-xs font-black text-[#00e676] mt-0.5">{second.total_points} Pts</p>
                  </div>
                  <div className="w-full bg-gradient-to-t from-slate-500 via-slate-400 to-slate-200 h-32 sm:h-40 rounded-t-2xl shadow-2xl border-t-4 border-white flex flex-col items-center justify-center text-slate-950 font-black">
                    <span className="text-3xl sm:text-4xl">2</span>
                    <span className="text-[10px] uppercase font-bold tracking-widest text-slate-800">Plata</span>
                  </div>
                </div>
              ) : (
                <div className="w-28 sm:w-40 h-32 sm:h-40 bg-[#07241c]/50 rounded-t-2xl border-t-4 border-slate-500/40 flex items-center justify-center text-slate-500 font-bold text-xl">
                  -
                </div>
              )}

              {/* 1° LUGAR (Oro - F1 Podium Center Highest Step) */}
              {first && (
                <div className="flex flex-col items-center w-36 sm:w-48 -mt-6">
                  <div className="text-center mb-3">
                    <div className="text-3xl sm:text-4xl mb-1 animate-bounce">🏆</div>
                    <span className="inline-block bg-amber-400 text-amber-950 text-xs font-black px-3 py-1 rounded-full uppercase tracking-widest shadow-lg mb-1">
                      1° LUGAR
                    </span>
                    <p className="font-black text-white text-sm sm:text-base truncate max-w-[140px] sm:max-w-[180px]" title={first.full_name}>
                      {toCapitalize(first.full_name)}
                    </p>
                    <p className="text-sm font-black text-[#00e676] mt-0.5">{first.total_points} Puntos</p>
                  </div>
                  <div className="w-full bg-gradient-to-t from-amber-600 via-amber-400 to-yellow-300 h-44 sm:h-52 rounded-t-2xl shadow-2xl border-t-4 border-yellow-100 flex flex-col items-center justify-center text-amber-950 font-black">
                    <span className="text-4xl sm:text-5xl">1</span>
                    <span className="text-xs uppercase font-extrabold tracking-widest text-amber-900">CAMPEÓN</span>
                  </div>
                </div>
              )}

              {/* 3° LUGAR (Bronce - F1 Podium Right Step) */}
              {third ? (
                <div className="flex flex-col items-center w-28 sm:w-40">
                  <div className="text-center mb-3">
                    <span className="inline-block bg-amber-700 text-amber-100 text-[10px] sm:text-xs font-black px-2.5 py-0.5 rounded-full uppercase tracking-wider shadow-md mb-1">
                      3° LUGAR
                    </span>
                    <p className="font-bold text-white text-xs sm:text-sm truncate max-w-[120px] sm:max-w-[150px]" title={third.full_name}>
                      {toCapitalize(third.full_name)}
                    </p>
                    <p className="text-xs font-black text-[#00e676] mt-0.5">{third.total_points} Pts</p>
                  </div>
                  <div className="w-full bg-gradient-to-t from-amber-900 via-amber-800 to-amber-600 h-24 sm:h-32 rounded-t-2xl shadow-2xl border-t-4 border-amber-400 flex flex-col items-center justify-center text-amber-100 font-black">
                    <span className="text-2xl sm:text-3xl">3</span>
                    <span className="text-[10px] uppercase font-bold tracking-widest text-amber-200">Bronce</span>
                  </div>
                </div>
              ) : (
                <div className="w-28 sm:w-40 h-24 sm:h-32 bg-[#07241c]/50 rounded-t-2xl border-t-4 border-amber-800/40 flex items-center justify-center text-slate-500 font-bold text-xl">
                  -
                </div>
              )}
            </div>
          </div>

          {/* Leaderboard Table (Rest of Top 10: 4th to 10th places) */}
          {rest.length > 0 && (
            <div className="bg-[#0b382c]/90 border-2 border-[#00e676]/40 rounded-3xl overflow-hidden shadow-2xl backdrop-blur-md">
              <div className="px-6 py-4 bg-[#07241c] border-b border-[#00e676]/30 font-black text-xs text-[#00e676] uppercase tracking-widest">
                Clasificación de Pilotos (Posiciones 4° al 10°)
              </div>
              <div className="divide-y divide-[#00e676]/10">
                {rest.map((user, idx) => (
                  <div key={user.user_id} className="flex items-center justify-between px-6 py-4 hover:bg-[#07241c]/50 transition-all">
                    <div className="flex items-center gap-3">
                      <span className="w-7 h-7 rounded-full bg-[#07241c] text-[#00e676] border border-[#00e676]/40 text-xs font-black flex items-center justify-center">
                        {idx + 4}
                      </span>
                      <div>
                        <p className="text-sm font-bold text-white">{toCapitalize(user.full_name)}</p>
                        <p className="text-xs text-slate-300 font-medium">{user.city || user.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-black text-[#00e676]">{user.total_points} Pts</span>
                      <p className="text-[10px] text-slate-400 font-medium">
                        {user.stations_visited} Estaciones Visitadas
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
